// VideoDetailScreen.jsx
import React, { useState, useContext, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";
import {
  getFirestore,
  getDoc,
  doc,
} from "firebase/firestore";
import {
  getFunctions,
  httpsCallable,
} from "firebase/functions";
import { AppContext } from "./AppContext";

const width  = Math.max(window.innerWidth, 320);
const height = Math.max(window.innerHeight, 640);
const bottomAreaHeight     = height * 0.18;
const scrollAreaTopPadding = height * 0;
const CACHE_DURATION = 12;

export default function VideoDetailScreen() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const incoming  = location.state?.dat || {};   // passed from previous screen
  const incomingId = incoming.contentIdd;

  const {
    assetsSummary,
    userWallet,
    setAssetsSummary,
    userId2,
    setCurrentlySelling,
  } = useContext(AppContext);



  /* live DAT (may update via assetsSummary) */
  const dat = useMemo(
    () => assetsSummary.find(a => a.contentId === incomingId) || incoming,
    [assetsSummary, incomingId, incoming]
  );

  /* ───────── local state ───────── */
  const [showSellModal, setShowSellModal] = useState(false);
  const [selectedEdition, setSelectedEdition] = useState(null);
  const [newPrice, setNewPrice] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  /* ------- helpers for profile popup ------- */
  const sortByTimestamp = arr =>
    [...arr].sort((a,b)=>{
      const t = x =>
        typeof x === "number"
          ? x
          : x?.toMillis?.() ??
            (x?.creationDate ? Date.parse(x.creationDate) : 0);
      return t(b.releaseTimestamp) - t(a.releaseTimestamp);
    });

  const buildPopupData = (u, uid) => ({
    artistName   : u.artistName || u.ArtistName,
    popupProfileImage: u.profileImageUrl,
    username     : u.username,
    accountType  : u.accountType,
    links        : u.links || [],
    userId       : uid,
    otherReleases: {
      albums : sortByTimestamp(u.albums  || []),
      singles: sortByTimestamp(u.singles || []),
      videos : sortByTimestamp(u.videos  || []),
      beats  : sortByTimestamp(u.beats   || []),
      spncasts: sortByTimestamp(u.spncasts||[])
    },
    vocalDrop    : u.vocalDrop,
    voiceDropMuted: u.voiceDropMuted,
    bio          : u.bio,
    blurb        : u.blurb,
    location     : u.location,
    ScreenSKN    : u.ScreenSKN,
    timestamp    : Date.now(),
  });

  const getCachedUserDoc = async uid => {
    const cached = [];
    const now    = Date.now();
    if (cached && now - cached.timestamp < CACHE_DURATION*60*1000) return cached;

    const snap = await getDoc(doc(getFirestore(), "users", uid));
    if (!snap.exists()) return null;
    const fresh = { ...snap.data(), timestamp: now };

    return fresh;
  };

  const fetchProfile = async uid => {
    if (!uid) return;
    if (uid === userId2) {
      navigate("/store");
      return;
    }
    const u = await getCachedUserDoc(uid);
    if (u) {
      // open your existing popup logic here if desired
      // setActivePopupMedia(buildPopupData(u, uid));
    }
  };

  /* ------- Sell DAT modal logic ------- */
  const ownedUnsold = userWallet?.ownedDats
    ?.filter(e => e.id === dat.id && !e.sold) || [];

  const openSellModal = ed => {
    setSelectedEdition(ed);
    setErrorMessage("");
    const oldPrice = ed?.price || 0;
    const defaultPrice = oldPrice + oldPrice * 0.8;
    setNewPrice(defaultPrice.toFixed(2));
    setShowSellModal(true);
  };

  const confirmSellDAT = async () => {
    if (!selectedEdition) return;
    const priceNum = parseFloat(newPrice) || 0;
    if (priceNum < (selectedEdition.price || 0)) {
      setErrorMessage(`Price must be at least $${selectedEdition.price.toFixed(2)}`);
      return;
    }

    try {

      setShowSellModal(false);

      const functions   = getFunctions();
      const setEdition  = httpsCallable(functions, "setEditionForSaleCF");
      await setEdition({
        datId        : dat.id,
        editionNumber: selectedEdition.edition,
        newPrice     : priceNum,
      });

      /* update local cache */
      setAssetsSummary(prev =>
        prev.map(a =>
          a.id === dat.id
            ? { ...a,
                editions: a.editions.map(ed =>
                  ed.edition === selectedEdition.edition
                    ? { ...ed, isForSale:true, price:priceNum }
                    : ed)
              }
            : a
        )
      );
      alert(`Your DAT (Edition #${selectedEdition.edition}) is now for sale!`);
    } catch (err) {
      console.error(err);
      alert("Failed to list DAT. Try again.");
    } finally {

    }
  };



  /* ------- rendering helpers ------- */
  const totalExtraRoyalties =
    (dat.ownedEditions||[]).reduce((s,ed)=>s+(ed.royalties||0),0);
  const totalOwnershipPercent = 70 + totalExtraRoyalties;
  const estEarnings = (totalOwnershipPercent/100) * (parseFloat(newPrice)||0);

  const matchingEditions = userWallet?.ownedDats
  ?.filter(entry => entry.id === dat.id) // Match the `dat.id`
  ?.filter(entry => (entry.sold ?? false) === false);

// ── Merge wallet’s earnings/sold flags onto each edition object ──
const ownedEditionsWithEarnings = (dat?.ownedEditions || []).map(edition => {
  const walletEntry = matchingEditions.find(e => e.edition === edition.edition);
  return {
    ...edition,
    earnings  : walletEntry?.earnings  ?? 0,
    sold      : walletEntry?.sold      ?? true,
    isForSale : walletEntry?.isForSale ?? edition.isForSale ?? false,
  };
});



  if (!dat.id) {
    return (
      <div style={styles.fallback}>
        <p style={{color:"#fff"}}>No DAT provided.</p>
      </div>
    );
  }

  return (
    <div style={styles.fullScreen}>
      <img src={require("./assets/BlankBG.png")} alt="" style={styles.bgImg}/>
      <div style={styles.contentArea}>

        {/* header */}
        <button style={styles.backBtn} onClick={()=>navigate(-1)}>
          <IoArrowBack size={24} color="#fff" style={{marginRight:8}}/>
          <span style={styles.backText}>{dat.SongName}</span>
        </button>

        {/* scrollable body */}
        <div style={styles.scrollView}>
          {/* artwork */}
          <div onClick={()=>navigate(`/dat/${dat.id}`, { state: { dat } })} style={styles.artworkWrap}>
            <div className="pulseScale" style={styles.artworkPulse}>
              <img src={dat.ArtworkURL} style={styles.artworkImg}/>
            </div>
          </div>
 
          {/* creator */}
          <div style={styles.creatorRow}> 
            <span style={styles.creatorTitle}>Creator:</span>
            <button
              style={styles.creatorBtn}
              onClick={()=>fetchProfile(dat.userId)}
            >
              <div style={styles.creatorAvatar}>
                <img src={dat.profileImageUrl} style={{width:"100%",height:"100%"}}/>
              </div>
              <span style={styles.creatorName}>{dat.ArtistName}</span>
            </button>
          </div>
          <hr style={styles.divider}/>

          {/* owned editions list */}
          {(ownedEditionsWithEarnings||[])
            .filter(ed=>ownedUnsold.some(w=>w.edition===ed.edition))
            .map(ed=>{
              const walletEntry = ownedUnsold.find(w=>w.edition===ed.edition);
              const canSell     = dat.SoldOut && !ed.isForSale && !walletEntry.sold;
              const isListed    = dat.SoldOut && ed.isForSale;

              return (
                <div key={ed.edition} style={styles.editionBox}>
                  <div>
                    <div style={styles.editionText}>Edition&nbsp;#{ed.edition}</div>
                    <div style={styles.editionText}>Royalties&nbsp;{ed.royalties||0}%</div>
                    <div style={styles.editionText}>
  Earnings&nbsp;
  {`$${Number(ed.earnings || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`}
</div>

                  </div>

                  {canSell && (
                    <button style={styles.sellBtn} onClick={()=>openSellModal(ed)}>
                      Sell&nbsp;DAT
                    </button>
                  )}
                  {isListed && (
                    <div style={styles.forSaleTag}>For&nbsp;Sale</div>
                  )}
                </div>
              );
            })}
        </div>
      </div>

      {/* gradient overlay */}


      {/* bottom spacer */}
      <div style={{height:bottomAreaHeight}}/>

      {/* SELL MODAL */}
      {showSellModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalCard}>
            <h3 style={styles.modalTitle}>List DAT for Sale</h3>
            <p style={styles.modalLabel}>
              New listing price: ${parseFloat(newPrice||0).toFixed(2)}
            </p>
            <input
              style={styles.priceInput}
              type="number"
              step="0.01"
              value={newPrice}
              onChange={e=>setNewPrice(e.target.value)}
            />
            <p style={styles.earningsText}>
              You will earn ≈ ${estEarnings.toFixed(2)} if it sells
            </p>
            {errorMessage && <p style={styles.errorMsg}>{errorMessage}</p>}

            <button style={styles.modalBtn} onClick={confirmSellDAT}>Sell&nbsp;DAT</button>
            <button style={{...styles.modalBtn,background:"#444"}} onClick={()=>setShowSellModal(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── styles ───────────────────────── */
const styles = {
  fullScreen:{ position:"relative", width:"100%", height:"100vh", overflow:"hidden", fontFamily:"brhendrix" },
  bgImg     :{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover", zIndex:0 },
  contentArea:{ paddingTop: '10px',position:"absolute", inset:0, display:"flex", flexDirection:"column", zIndex:1 },
  backBtn   :{
    display:"flex",alignItems:"center",background:"#0e061e",
    border:"none",color:"#fff",padding:"12px 16px",cursor:"pointer",
    fontSize:'23px',fontFamily:"brhendrix"
  },
  backText  :{ flex:1,textAlign:"center" },
  scrollView:{
    flex:1, overflowY:"auto",
    paddingTop:scrollAreaTopPadding,
    paddingBottom:bottomAreaHeight+20
  },
  artworkWrap:{ width:"100%",height:height*0.45,cursor:"pointer" },
  artworkPulse:{ width:"100%",height:"100%",borderRadius:10,overflow:"hidden" },
  artworkImg :{ width:"100%",height:"100%",objectFit:"contain" },
  creatorRow :{ display:"flex",alignItems:"center",margin:`${height*0.01}px ${width*0.02}px` },
  creatorTitle:{ color:"#fff",fontWeight:700,fontSize:'18px' },
  creatorBtn :{ display:"flex",alignItems:"center",background:"none",border:"none",cursor:"pointer",marginLeft:10 },
  creatorAvatar:{ width:40,height:40,borderRadius:20,overflow:"hidden",border:"2px solid #62fbfa",marginRight:10 },
  creatorName:{ color:"#fff",fontSize:'18px' },
  divider    :{ border:"none",borderBottom:"1px solid #ddd",margin:"10px 0" },
  editionBox :{
    display:"flex",justifyContent:"space-between",alignItems:"center",
    background:"#211043",borderRadius:10,padding:10,
    margin:`${height*0.003}px ${width*0.02}px`
  },
  editionText:{ color:"#fff",fontSize:18 },
  sellBtn    :{
    background:"#4F8EF7",color:"#fff",border:"none",borderRadius:5,
    padding:"8px 12px",cursor:"pointer",fontWeight:600
  },
  forSaleTag :{
    background:"#4caf50",color:"#fff",padding:"8px 12px",borderRadius:5,fontWeight:600
  },
  fadeImg    :{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover", zIndex:2, pointerEvents:"none" },
  fallback   :{ display:"flex",alignItems:"center",justifyContent:"center",width:"100%",height:"100vh" },

  /* modal */
  modalOverlay:{ position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",display:"flex",justifyContent:"center",alignItems:"center",zIndex:10 },
  modalCard  :{ width:'475px',maxWidth:500,background:"#1c1c1c",padding:20,borderRadius:10,textAlign:"center" },
  modalTitle :{ color:"#fff",marginBottom:10, fontSize: '18px' },
  modalLabel :{ color:"#fff",marginBottom:10,fontSize:'18px' },
  priceInput :{ width:"100%",padding:10,borderRadius:5,background:"#333",color:"#fff",border:"none",marginBottom:15 },
  earningsText:{ color:"#62fbfa",marginBottom:18,fontSize:'18px' },
  errorMsg   :{ color:"red",marginBottom:10 },
  modalBtn   :{ width:"100%",padding:12,border:"none",borderRadius:5,background:"#4F8EF7",color:"#fff",fontWeight:600,cursor:"pointer",marginBottom:10 }
};
 
/* CSS animation (add to VideoDetailScreen.css or global stylesheet)
.pulseScale{
  animation: pulse 1s ease-out infinite;
}
@keyframes pulse{
  0%  { transform: scale(0.96); }
  50% { transform: scale(1); }
  100%{ transform: scale(0.96); }
}
*/
